import { studentApi } from '@/lib/api';
import { ticketApi } from '@/lib/api';
import { adminSocket } from '@/lib/admin-socket';
import config from '@/config';
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import { toast } from 'sonner';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Paperclip } from 'lucide-react';
import type { Ticket, TicketAttachmentRef, TicketCategory, TicketMessage, TicketStatus, WebSocketMessage } from '@/types/ticket';
import { useTicketMessagePagination } from '@/hooks/useTicketMessagePagination';
import { AdminTicketsDashboard } from '@/components/tickets/AdminTicketsDashboard';
import { StudentDetailsPanel, StudentDetailsSkeleton } from '@/components/tickets/StudentDetailsPanel';
import { TicketChatHeader } from '@/components/tickets/TicketChatHeader';
import { TicketChatSkeleton } from '@/components/tickets/TicketChatSkeleton';
import { TicketConversationMessages } from '@/components/tickets/TicketConversationMessages';
import { TicketInboxSidebar, type InboxSortOrder } from '@/components/tickets/TicketInboxSidebar';
import {
  TICKET_ATTACHMENT_ACCEPT,
  putFileToPresignedUrl,
  toAttachmentRef,
  validateTicketAttachmentFile,
} from '@/lib/ticketAttachments';

const SEND_REPLY_TIMEOUT_MS = 30_000;

const TICKET_VIEW_TABS = ['dashboard', 'inbox'] as const;

function mergeAdminTicketPatch(existing: Ticket, patch: Partial<Ticket>): Ticket {
  const merged = { ...existing, ...patch };
  if (patch.unreadForAdmin === undefined) merged.unreadForAdmin = existing.unreadForAdmin;
  return merged;
}

function applyTicketListMessageUpdate(
  tickets: Ticket[],
  msg: Pick<WebSocketMessage, 'ticketId' | 'senderType' | 'message' | 'createdAt'>,
  sortOrder: InboxSortOrder,
  activeTicketId?: string | null
): Ticket[] {
  const activityAt = msg.createdAt ?? new Date().toISOString();
  const mapped = tickets.map((t) =>
    t.ticketId === msg.ticketId
      ? {
          ...t,
          lastMessageSenderType: msg.senderType,
          status: msg.senderType === 'admin' ? ('replied' as const) : t.status,
          lastMessagePreview: msg.message,
          unreadForAdmin:
            msg.senderType === 'student' && msg.ticketId !== activeTicketId,
          timestamps: {
            ...t.timestamps,
            lastMessageAt: activityAt,
            updatedAt: activityAt,
          },
        }
      : t
  );

  if (sortOrder !== 'newest') return mapped;

  const idx = mapped.findIndex((t) => t.ticketId === msg.ticketId);
  if (idx <= 0) return mapped;

  const next = [...mapped];
  const [item] = next.splice(idx, 1);
  next.unshift(item);
  return next;
}

function previewFromAdminReply(text: string, attachment?: TicketAttachmentRef | null): string {
  if (text.trim()) return text.trim();
  if (attachment?.filename) return `Attachment: ${attachment.filename}`;
  return 'Admin replied';
}

export function AdminSupportTicketsView() {
  const [tab, setTab] = useQueryState(
    'tab',
    parseAsStringLiteral(TICKET_VIEW_TABS).withDefault('dashboard')
  );
  const [selectedId, setSelectedId] = useQueryState('ticket', parseAsString);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [detail, setDetail] = useState<{
    ticket: Ticket;
    studentContext?: Record<string, unknown>;
    ticketHistory?: { totalTickets: number; openTickets: number };
  } | null>(null);
  const [scrollToBottomSignal, setScrollToBottomSignal] = useState(0);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadedTicketId, setLoadedTicketId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<TicketStatus | ''>('');
  const [filterCategory, setFilterCategory] = useState<TicketCategory | ''>('');
  const [sortOrder, setSortOrder] = useState<InboxSortOrder>('newest');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [replyAttachment, setReplyAttachment] = useState<TicketAttachmentRef | null>(null);
  const [uploadingReplyFile, setUploadingReplyFile] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const atBottomRef = useRef(true);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const detailRequestRef = useRef(0);
  const sendReplyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingReplyRef = useRef<{ text: string; attachment: TicketAttachmentRef | null } | null>(
    null
  );
  const selectedIdRef = useRef<string | null>(selectedId);
  const sortOrderRef = useRef(sortOrder);
  const loadingDetailRef = useRef(loadingDetail);
  const loadedTicketIdRef = useRef(loadedTicketId);
  const [ticketSocketReady, setTicketSocketReady] = useState(false);

  const fetchTicketMessages = useCallback(
    async (ticketId: string, params?: { beforeMessageId?: string }) => {
      const res = await ticketApi.getTicketMessages(ticketId, params);
      return {
        messages: (res.data.messages ?? []) as TicketMessage[],
        pagination: res.data.pagination,
      };
    },
    []
  );

  const {
    messages: threadMessages,
    pagination: messagesPagination,
    loadingOlder,
    resetMessages,
    prependOlderMessages,
    appendMessage,
    patchMessage,
  } = useTicketMessagePagination(fetchTicketMessages);

  const appendMessageRef = useRef(appendMessage);
  const patchMessageRef = useRef(patchMessage);
  const bumpScrollToBottomRef = useRef(() => {
    if (atBottomRef.current) setScrollToBottomSignal((n) => n + 1);
  });

  useEffect(() => {
    appendMessageRef.current = appendMessage;
    patchMessageRef.current = patchMessage;
    bumpScrollToBottomRef.current = () => {
      if (atBottomRef.current) setScrollToBottomSignal((n) => n + 1);
    };
  }, [appendMessage, patchMessage]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);
  useEffect(() => {
    loadingDetailRef.current = loadingDetail;
  }, [loadingDetail]);
  useEffect(() => {
    loadedTicketIdRef.current = loadedTicketId;
  }, [loadedTicketId]);

  const clearSendReplyPending = useCallback(() => {
    if (sendReplyTimeoutRef.current) {
      clearTimeout(sendReplyTimeoutRef.current);
      sendReplyTimeoutRef.current = null;
    }
    pendingReplyRef.current = null;
    setSendingReply(false);
  }, []);

  const loadList = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const res = await ticketApi.getTickets({
        limit: 50,
        sortBy: 'lastMessageAt',
        sortOrder: sortOrder === 'newest' ? 'desc' : 'asc',
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(filterCategory ? { category: filterCategory } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      setTickets(res.data.tickets ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tickets');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [filterStatus, filterCategory, sortOrder, debouncedSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadDetail = useCallback(async (ticketId: string) => {
    const requestId = ++detailRequestRef.current;
    try {
      const res = await ticketApi.getTicket(ticketId);
      if (requestId !== detailRequestRef.current) return;
      const { messages, messagesPagination, ticket, studentContext, ticketHistory } = res.data;
      setDetail({ ticket, studentContext, ticketHistory });
      resetMessages(
        (messages ?? []) as TicketMessage[],
        messagesPagination ?? {
          hasMoreOlder: false,
          totalCount: messages?.length ?? 0,
          limit: messages?.length ?? 0,
        }
      );
      atBottomRef.current = true;
      setScrollToBottomSignal(0);
      setLoadedTicketId(ticketId);
      setTickets((prev) =>
        prev.map((t) => (t.ticketId === ticketId ? { ...t, unreadForAdmin: false } : t))
      );
      if (ticketSocketReady) {
        adminSocket.joinTicket(ticketId);
      }
    } catch (e) {
      if (requestId !== detailRequestRef.current) return;
      toast.error(e instanceof Error ? e.message : 'Failed to load ticket');
    } finally {
      if (requestId === detailRequestRef.current) {
        setLoadingDetail(false);
      }
    }
  }, [ticketSocketReady, resetMessages]);

  const showChatSkeleton = Boolean(
    selectedId && (loadingDetail || loadedTicketId !== selectedId)
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId && tab !== 'inbox') {
      void setTab('inbox');
    }
  }, [selectedId, tab, setTab]);

  useEffect(() => {
    if (!selectedId) {
      setLoadedTicketId(null);
      setDetail(null);
      resetMessages([], { hasMoreOlder: false, totalCount: 0, limit: 40 });
      setLoadingDetail(false);
      return;
    }

    setLoadingDetail(true);
    setEditingMessageId(null);
    setEditText('');
    setReply('');
    setReplyAttachment(null);
    void loadDetail(selectedId);
  }, [selectedId, loadDetail, resetMessages]);

  useEffect(() => {
    if (!ticketSocketReady || !selectedId) return;
    adminSocket.joinTicket(selectedId);
    return () => {
      adminSocket.leaveTicket(selectedId);
    };
  }, [ticketSocketReady, selectedId]);

  useEffect(() => {
    let cancelled = false;

    const appendMessageToOpenChat = (msg: WebSocketMessage) => {
      const activeId = selectedIdRef.current;
      if (msg.ticketId !== activeId) return;
      if (loadingDetailRef.current || loadedTicketIdRef.current !== activeId) return;
      appendMessageRef.current(msg as TicketMessage);
      bumpScrollToBottomRef.current();
    };

    const onMsg = (msg: WebSocketMessage) => {
      const activeId = selectedIdRef.current;
      setTickets((prev) =>
        applyTicketListMessageUpdate(prev, msg, sortOrderRef.current, activeId)
      );

      if (msg.ticketId === activeId && msg.senderType === 'admin') {
        clearSendReplyPending();
        setDetail((prev) =>
          prev && prev.ticket.ticketId === activeId
            ? { ...prev, ticket: { ...prev.ticket, status: 'replied' } }
            : prev
        );
      }

      appendMessageToOpenChat(msg);
    };

    const applyStudentReadAt = (ticketId: string, ticketPatch?: Partial<Ticket>) => {
      if (ticketPatch?.studentLastReadAt === undefined) return;
      setDetail((prev) => {
        if (!prev || prev.ticket.ticketId !== ticketId) return prev;
        return {
          ...prev,
          ticket: {
            ...prev.ticket,
            studentLastReadAt: ticketPatch.studentLastReadAt ?? null,
          },
        };
      });
    };

    const onTicketUpdated = (data: {
      ticketId: string;
      message?: WebSocketMessage;
      ticket?: Partial<Ticket>;
    }) => {
      if (data.message) {
        onMsg(data.message);
        applyStudentReadAt(data.ticketId, data.ticket);
        return;
      }
      if (!data.ticket) return;
      setTickets((prev) =>
        prev.map((t) =>
          t.ticketId === data.ticketId ? mergeAdminTicketPatch(t, data.ticket!) : t
        )
      );
      applyStudentReadAt(data.ticketId, data.ticket);
    };

    const onMessageSent = (data: { ticketId: string; messageId: string }) => {
      if (data.ticketId === selectedIdRef.current) {
        clearSendReplyPending();
      }
    };
    const onSocketError = (error: { message?: string; code?: string }) => {
      if (!pendingReplyRef.current) return;
      const restored = pendingReplyRef.current;
      setReply(restored.text);
      setReplyAttachment(restored.attachment);
      clearSendReplyPending();
      toast.error(error.message ?? 'Send failed');
    };
    const onEdited = (data: { ticketId: string; message: TicketMessage }) => {
      if (data.ticketId !== selectedIdRef.current) return;
      patchMessageRef.current(data.message.messageId, data.message);
    };
    const onDeleted = (data: { ticketId: string; messageId: string }) => {
      if (data.ticketId !== selectedIdRef.current) return;
      patchMessageRef.current(data.messageId, { isDeleted: true });
    };
    const onUnsent = (data: {
      ticketId: string;
      messageId: string;
      unsentAt?: string;
      isUnsent?: boolean;
    }) => {
      if (data.ticketId !== selectedIdRef.current) return;
      patchMessageRef.current(data.messageId, {
        isUnsent: data.isUnsent ?? true,
        unsentAt: data.unsentAt ?? new Date().toISOString(),
      });
    };

    const attachListeners = () => {
      adminSocket.onNewMessage(onMsg);
      adminSocket.onTicketUpdated(onTicketUpdated);
      adminSocket.onMessageEdited(onEdited);
      adminSocket.onMessageDeleted(onDeleted);
      adminSocket.onMessageUnsent(onUnsent);
      adminSocket.onMessageSent(onMessageSent);
      adminSocket.onError(onSocketError);
    };

    const detachListeners = () => {
      adminSocket.offNewMessage(onMsg);
      adminSocket.offTicketUpdated(onTicketUpdated);
      adminSocket.offMessageEdited(onEdited);
      adminSocket.offMessageDeleted(onDeleted);
      adminSocket.offMessageUnsent(onUnsent);
      adminSocket.offMessageSent(onMessageSent);
      adminSocket.offError(onSocketError);
    };

    const rejoinSelectedTicket = () => {
      const activeId = selectedIdRef.current;
      if (activeId) adminSocket.joinTicket(activeId);
    };

    const onConnect = () => {
      if (cancelled) return;
      setTicketSocketReady(true);
      rejoinSelectedTicket();
    };

    void (async () => {
      try {
        await adminSocket.connect();
        if (cancelled) return;
        attachListeners();
        adminSocket.onSocketConnect(onConnect);
        adminSocket.onReconnect(rejoinSelectedTicket);
        if (adminSocket.connected) {
          onConnect();
        }
      } catch (error) {
        console.warn('Support ticket socket connect failed:', error);
      }
    })();

    return () => {
      cancelled = true;
      setTicketSocketReady(false);
      detachListeners();
      adminSocket.offSocketConnect(onConnect);
      adminSocket.offReconnect(rejoinSelectedTicket);
    };
  }, [clearSendReplyPending]);

  const sendReply = async () => {
    if (!selectedId || (!reply.trim() && !replyAttachment) || sendingReply) return;

    const text = reply.trim();
    const attachment = replyAttachment;
    const preview = previewFromAdminReply(text, attachment);
    const optimisticActivityAt = new Date().toISOString();

    setSendingReply(true);
    pendingReplyRef.current = { text, attachment };

    setTickets((prev) =>
      applyTicketListMessageUpdate(
        prev,
        {
          ticketId: selectedId,
          senderType: 'admin',
          message: preview,
          createdAt: optimisticActivityAt,
        },
        sortOrder,
        selectedId
      )
    );

    try {
      if (adminSocket.connected && !attachment) {
        setReply('');
        setReplyAttachment(null);
        adminSocket.sendMessage(selectedId, text);

        sendReplyTimeoutRef.current = setTimeout(() => {
          if (!pendingReplyRef.current) return;
          const restored = pendingReplyRef.current;
          setReply(restored.text);
          setReplyAttachment(restored.attachment);
          clearSendReplyPending();
          toast.error('Send timed out. Please try again.');
        }, SEND_REPLY_TIMEOUT_MS);
        return;
      }

      const res = await ticketApi.sendMessage(
        selectedId,
        text,
        attachment ? [attachment] : undefined
      );
      const saved = res.data?.message as TicketMessage | undefined;

      setReply('');
      setReplyAttachment(null);
      clearSendReplyPending();

      if (saved) {
        setTickets((prev) =>
          applyTicketListMessageUpdate(
            prev,
            {
              ticketId: selectedId,
              senderType: 'admin',
              message: saved.message,
              createdAt: saved.createdAt ?? optimisticActivityAt,
            },
            sortOrder,
            selectedId
          )
        );

        setDetail((prev) =>
          prev
            ? { ...prev, ticket: { ...prev.ticket, status: 'replied' } }
            : prev
        );
        appendMessage(saved);
        bumpScrollToBottomRef.current();
      }
    } catch (e) {
      const restored = pendingReplyRef.current;
      if (restored) {
        setReply(restored.text);
        setReplyAttachment(restored.attachment);
      }
      clearSendReplyPending();
      toast.error(e instanceof Error ? e.message : 'Send failed');
    }
  };

  const handleReplyFile = async (file: File) => {
    if (!selectedId) return;
    const validationError = validateTicketAttachmentFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      setUploadingReplyFile(true);
      const res = await ticketApi.presignAttachment(selectedId, {
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      await putFileToPresignedUrl(res.data.uploadUrl, file, file.type);
      setReplyAttachment(toAttachmentRef(file, res.data.storageKey));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploadingReplyFile(false);
    }
  };

  const markInProcess = async () => {
    if (!selectedId) return;
    await ticketApi.markInProcess(selectedId);
    await loadDetail(selectedId);
    void loadList({ silent: true });
  };

  const closeTicket = async () => {
    if (!selectedId) return;
    await ticketApi.closeTicket(selectedId);
    await loadDetail(selectedId);
    void loadList({ silent: true });
  };

  const restoreTicket = async (ticketId?: string) => {
    const id = ticketId ?? selectedId;
    if (!id) return;
    await ticketApi.restoreTicket(id);
    if (selectedId === id) {
      await loadDetail(id);
    }
    void loadList({ silent: true });
  };

  const viewAsUser = async () => {
    const studentId = detail?.ticket?.createdBy?.userId;
    if (!studentId) {
      toast.error('Student ID not found for this ticket');
      return;
    }
    if (!config.studentAppUrl) {
      toast.error('Student portal URL is not configured (VITE_STUDENT_APP_URL).');
      return;
    }

    try {
      const res = await studentApi.impersonate(studentId);
      const baseUrl = res?.data?.impersonationUrl;
      if (!baseUrl) {
        throw new Error('No impersonation URL returned');
      }

      // Impersonation URL already includes ?token=… — do not append paths onto it.
      const returnTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const url = new URL(baseUrl);
      url.searchParams.set('returnTo', returnTo);
      url.searchParams.set('sessionMode', 'tab');
      window.open(url.toString(), '_blank', 'noopener,noreferrer');

      const studentName =
        (detail.studentContext?.student as { fullName?: string } | undefined)?.fullName ||
        detail.ticket?.createdBy?.userName ||
        'student';
      toast.success(`Opened student portal as ${studentName}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Impersonation failed');
    }
  };

  const startEdit = (m: TicketMessage) => {
    setEditingMessageId(m.messageId);
    setEditText(m.message);
  };

  const saveEdit = async () => {
    if (!selectedId || !editingMessageId || !editText.trim()) return;
    try {
      const res = (await ticketApi.editMessage(
        selectedId,
        editingMessageId,
        editText.trim()
      )) as { data?: { message?: TicketMessage } };
      const updated = res.data?.message as TicketMessage | undefined;
      if (updated) {
        patchMessage(updated.messageId, updated);
      } else {
        patchMessage(editingMessageId, {
          message: editText.trim(),
          isEdited: true,
        });
      }
      setEditingMessageId(null);
      setEditText('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Edit failed');
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!selectedId) return;
    try {
      await ticketApi.deleteMessage(selectedId, messageId);
      patchMessage(messageId, { isDeleted: true });
      toast.success('Message deleted');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const unsendMessage = async (messageId: string) => {
    if (!selectedId) return;
    try {
      await ticketApi.unsendMessage(selectedId, messageId);
      patchMessage(messageId, {
        isUnsent: true,
        unsentAt: new Date().toISOString(),
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Unsend failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <div
          className="relative w-full max-w-[390px] rounded-full border border-border bg-card p-1.5 shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
          role="tablist"
          aria-label="Support tickets views"
        >
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute top-1.5 bottom-1.5 left-1.5 w-[calc(50%-6px)] rounded-full bg-primary shadow-md transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
              tab === 'inbox' && 'translate-x-full'
            )}
          />
          <div className="relative grid grid-cols-2">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'dashboard'}
              onClick={() => {
                void setTab('dashboard');
                void setSelectedId(null);
              }}
              className={cn(
                'relative z-10 rounded-full px-5 py-3.5 text-base font-bold tracking-tight transition-colors',
                tab === 'dashboard' ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Dashboard
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'inbox'}
              onClick={() => void setTab('inbox')}
              className={cn(
                'relative z-10 rounded-full px-5 py-3.5 text-base font-bold tracking-tight transition-colors',
                tab === 'inbox' ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Inbox
            </button>
          </div>
        </div>
      </div>

      {tab === 'dashboard' && (
        <AdminTicketsDashboard
          onOpenTicket={(ticketId) => {
            void setSelectedId(ticketId);
            void setTab('inbox');
          }}
        />
      )}

      {tab === 'inbox' && (
        <div className="grid h-[calc(100dvh-11rem)] min-h-[520px] grid-cols-1 gap-0 overflow-hidden rounded-xl border lg:grid-cols-[340px_1fr_300px]">
          <TicketInboxSidebar
            tickets={tickets}
            selectedId={selectedId}
            loading={loading}
            filterStatus={filterStatus}
            filterCategory={filterCategory}
            sortOrder={sortOrder}
            search={search}
            onSearchChange={setSearch}
            onFilterStatusChange={setFilterStatus}
            onFilterCategoryChange={setFilterCategory}
            onSortOrderChange={setSortOrder}
            onSelectTicket={(ticketId) => {
              void setSelectedId(ticketId);
              setTickets((prev) =>
                prev.map((t) =>
                  t.ticketId === ticketId ? { ...t, unreadForAdmin: false } : t
                )
              );
            }}
            onRestoreTicket={(ticketId) => void restoreTicket(ticketId)}
          />

          <section className="flex min-h-0 flex-col overflow-hidden bg-background">
            {!selectedId ? (
              <div className="flex flex-1 items-center justify-center text-muted-foreground">Select a ticket</div>
            ) : showChatSkeleton ? (
              <TicketChatSkeleton />
            ) : !detail ? (
              <div className="flex flex-1 items-center justify-center text-muted-foreground">Select a ticket</div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <TicketChatHeader
                  title={detail.ticket.title}
                  ticketId={detail.ticket.ticketId}
                  category={detail.ticket.category}
                  status={detail.ticket.status}
                  studentName={
                    (detail.studentContext?.student as { fullName?: string } | undefined)?.fullName ??
                    detail.ticket.createdBy?.userName
                  }
                  deletedByStudent={detail.ticket.deletedByStudent}
                  onMarkInProcess={() => void markInProcess()}
                  onClose={() => void closeTicket()}
                  onRestore={() => void restoreTicket()}
                />
                <TicketConversationMessages
                  messages={threadMessages}
                  hasMoreOlder={Boolean(messagesPagination?.hasMoreOlder)}
                  loadingOlder={loadingOlder}
                  onLoadOlder={() =>
                    selectedId ? prependOlderMessages(selectedId) : Promise.resolve(false)
                  }
                  scrollToBottomKey={loadedTicketId}
                  scrollToBottomSignal={scrollToBottomSignal}
                  onAtBottomChange={(atBottom) => {
                    atBottomRef.current = atBottom;
                  }}
                  studentLastReadAt={detail.ticket.studentLastReadAt}
                  editingMessageId={editingMessageId}
                  editText={editText}
                  onEditTextChange={setEditText}
                  onSaveEdit={() => void saveEdit()}
                  onCancelEdit={() => {
                    setEditingMessageId(null);
                    setEditText('');
                  }}
                  onStartEdit={startEdit}
                  onUnsend={(messageId) => void unsendMessage(messageId)}
                  onDelete={(messageId) => void deleteMessage(messageId)}
                />
                {detail.ticket.status !== 'closed' && (
                  <div className="shrink-0 border-t bg-background px-4 py-4">
                    {replyAttachment && (
                      <p className="mb-2 text-xs text-muted-foreground">
                        📎 {replyAttachment.filename}
                        <button
                          type="button"
                          className="ml-2 text-destructive"
                          onClick={() => setReplyAttachment(null)}
                        >
                          Remove
                        </button>
                      </p>
                    )}
                    <div className="flex items-end gap-2.5">
                      <input
                        ref={replyFileInputRef}
                        type="file"
                        accept={TICKET_ATTACHMENT_ACCEPT}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void handleReplyFile(file);
                          e.target.value = '';
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={uploadingReplyFile}
                        onClick={() => replyFileInputRef.current?.click()}
                        title="Attach file"
                        className="h-11 w-11 shrink-0 rounded-full border-primary/30 bg-accent text-primary hover:bg-accent/80"
                      >
                        <Paperclip className="h-4 w-4" />
                      </Button>
                      <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        disabled={sendingReply}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            void sendReply();
                          }
                        }}
                        className="min-h-[48px] max-h-32 flex-1 resize-y rounded-[18px] border border-border bg-background px-4 py-3 text-sm leading-snug outline-none focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-60"
                        placeholder="Write a reply to the student..."
                      />
                      <button
                        type="button"
                        onClick={() => void sendReply()}
                        disabled={
                          (!reply.trim() && !replyAttachment) || uploadingReplyFile || sendingReply
                        }
                        className="h-[46px] min-w-[108px] shrink-0 rounded-full bg-primary-gradient px-4 text-sm font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {sendingReply ? 'Sending…' : 'Send Reply'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          <aside className="hidden min-h-0 overflow-hidden border-l bg-muted lg:block">
            {!selectedId || showChatSkeleton ? (
              <StudentDetailsSkeleton />
            ) : detail?.studentContext ? (
              <StudentDetailsPanel
                studentContext={detail.studentContext}
                ticketHistory={detail.ticketHistory}
                onViewAsUser={() => void viewAsUser()}
              />
            ) : (
              <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                Select a ticket
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
